// Web Audio API context management and sound playback service
export interface AudioState {
  isPlaying: boolean
  currentSound: string | null
  error: string | null
}

class AudioService {
  private audioContext: AudioContext | null = null
  private audioElements: Map<string, HTMLAudioElement> = new Map()
  private currentPlaying: Set<string> = new Set()
  private cacheService: any = null

  constructor() {
    this.initializeAudioContext()
    this.initializeCacheService()
  }

  private async initializeCacheService() {
    try {
      const { cacheService } = await import('./cacheService')
      this.cacheService = cacheService
      await this.cacheService.init()
      console.log('Audio service: Cache service initialized')
    } catch (error) {
      console.warn('Audio service: Cache service not available:', error)
    }
  }

  private async initializeAudioContext() {
    try {
      // Create audio context with user gesture
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      // Resume context if suspended (required for mobile browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume()
      }

      console.log('Audio context initialized:', this.audioContext.state)
    } catch (error) {
      console.error('Failed to initialize audio context:', error)
    }
  }

  private async ensureAudioContext() {
    if (!this.audioContext) {
      await this.initializeAudioContext()
    }

    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume()
    }
  }

  private createAudioElement(soundPath: string): HTMLAudioElement {
    const audio = new Audio(soundPath)
    audio.preload = 'auto'
    audio.crossOrigin = 'anonymous'

    // Handle audio events
    audio.addEventListener('loadstart', () => {
      console.log('Audio loading started:', soundPath)
    })

    audio.addEventListener('canplaythrough', () => {
      console.log('Audio ready to play:', soundPath)
    })

    audio.addEventListener('error', () => {
      const error = audio.error
      console.error('Audio error:', soundPath, {
        error: error?.message || 'Unknown audio error',
        code: error?.code,
        timestamp: new Date().toISOString()
      })
    })

           audio.addEventListener('ended', () => {
             console.log('Audio ended:', soundPath)
             this.currentPlaying.delete(soundPath)
             // Dispatch custom event to notify components
             const event = new CustomEvent('audioEnded', {
               detail: { soundPath }
             })
             window.dispatchEvent(event)
           })

    audio.addEventListener('abort', () => {
      console.log('Audio loading aborted:', soundPath)
      this.currentPlaying.delete(soundPath)
    })

    return audio
  }

  private createAudioElementFromBlob(blob: Blob, soundPath: string): HTMLAudioElement {
    const audio = new Audio()
    audio.src = URL.createObjectURL(blob)
    audio.preload = 'auto'
    audio.crossOrigin = 'anonymous'

    // Handle audio events
    audio.addEventListener('loadstart', () => {
      console.log('Cached audio loading started:', soundPath)
    })

    audio.addEventListener('canplaythrough', () => {
      console.log('Cached audio ready to play:', soundPath)
    })

    audio.addEventListener('error', () => {
      const error = audio.error
      console.error('Cached audio error:', soundPath, {
        error: error?.message || 'Unknown audio error',
        code: error?.code,
        timestamp: new Date().toISOString()
      })
    })

    audio.addEventListener('ended', () => {
      console.log('Cached audio ended:', soundPath)
      this.currentPlaying.delete(soundPath)
    })

    audio.addEventListener('abort', () => {
      console.log('Cached audio loading aborted:', soundPath)
      this.currentPlaying.delete(soundPath)
    })

    return audio
  }

  private async cacheSoundAfterLoad(audio: HTMLAudioElement, soundData: any): Promise<void> {
    if (!this.cacheService) return

    try {
      // Wait for audio to load
      await new Promise((resolve, reject) => {
        const handleCanPlay = () => {
          audio.removeEventListener('canplaythrough', handleCanPlay)
          audio.removeEventListener('error', handleError)
          resolve(void 0)
        }

        const handleError = () => {
          audio.removeEventListener('canplaythrough', handleCanPlay)
          audio.removeEventListener('error', handleError)
          reject(new Error('Audio failed to load'))
        }

        if (audio.readyState >= 3) { // HAVE_FUTURE_DATA
          resolve(void 0)
        } else {
          audio.addEventListener('canplaythrough', handleCanPlay)
          audio.addEventListener('error', handleError)
        }
      })

      // Fetch the audio data
      const response = await fetch(audio.src)
      const audioData = await response.arrayBuffer()

      // Cache the sound
      await this.cacheService.cacheSound(soundData, audioData)
      console.log(`Sound cached successfully: ${soundData.label}`)
    } catch (error) {
      console.warn(`Failed to cache sound: ${soundData.label}`, error)
    }
  }

  async playSound(soundPath: string, soundData?: any): Promise<void> {
    try {
      console.log('Attempting to play sound:', soundPath)
      await this.ensureAudioContext()

      // Get or create audio element
      let audio = this.audioElements.get(soundPath)
      if (!audio) {
        console.log('Creating new audio element for:', soundPath)

        // Try to use cached sound first
        if (this.cacheService && soundData) {
          const cachedBlob = await this.cacheService.getCachedSoundBlob(soundData.id)
          if (cachedBlob) {
            console.log('Using cached sound:', soundPath)
            audio = this.createAudioElementFromBlob(cachedBlob, soundPath)
          } else {
            audio = this.createAudioElement(soundPath)
            // Cache the sound after loading
            this.cacheSoundAfterLoad(audio, soundData)
          }
        } else {
          audio = this.createAudioElement(soundPath)
        }

        this.audioElements.set(soundPath, audio)
      }

      // Reset audio to beginning
      audio.currentTime = 0

      // Play the sound
      await audio.play()
      this.currentPlaying.add(soundPath)

      console.log('Successfully playing sound:', soundPath)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Failed to play sound:', soundPath, {
        error: errorMessage,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      })

      // Provide more specific error messages
      if (errorMessage.includes('NotAllowedError')) {
        throw new Error('Audio playback blocked. Please interact with the page first.')
      } else if (errorMessage.includes('NotSupportedError')) {
        throw new Error('Audio format not supported by this browser.')
      } else if (errorMessage.includes('NetworkError')) {
        throw new Error('Network error loading audio file.')
      } else {
        throw new Error(`Failed to play sound: ${errorMessage}`)
      }
    }
  }

  stopSound(soundPath: string): void {
    const audio = this.audioElements.get(soundPath)
    if (audio) {
      audio.pause()
      audio.currentTime = 0
      this.currentPlaying.delete(soundPath)
      console.log('Stopped sound:', soundPath)
    } else {
      console.warn('Attempted to stop sound that is not loaded:', soundPath)
    }
  }

  stopAllSounds(): void {
    this.audioElements.forEach((audio) => {
      audio.pause()
      audio.currentTime = 0
    })
    this.currentPlaying.clear()
    console.log('Stopped all sounds')
  }

  isSoundPlaying(soundPath: string): boolean {
    return this.currentPlaying.has(soundPath)
  }

  getAudioState(soundPath: string): AudioState {
    return {
      isPlaying: this.isSoundPlaying(soundPath),
      currentSound: this.isSoundPlaying(soundPath) ? soundPath : null,
      error: null
    }
  }

  // Clean up resources
  destroy(): void {
    this.stopAllSounds()
    this.audioElements.clear()

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
  }
}

// Export singleton instance
export const audioService = new AudioService()
